// sourceQuery.js -- NFI-5884: turn a review-grid cell click into the locator query for the source panel.
// The whole clicked row goes to the locator as one group (its other values pin which page the row came from); the clicked cell is the focus.

const SKIP_FIELDS = new Set(['id', '__check__', '__actions__', 'actions', '__detail_panel_toggle__']);

function isLocatable(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'string') return value.trim().length > 0;
  return false; // booleans, objects, arrays: not printed values
}

function skipField(field) {
  if (!field || SKIP_FIELDS.has(field)) return true;
  return /_validation$/i.test(field);
}

export function labelFor(field, headerName) {
  if (headerName && String(headerName).trim()) return String(headerName);
  return String(field).replace(/_/g, ' ');
}

/**
 * params: the MUI DataGrid cell-click params ({ field, value, id, row, colDef }); headerFor(field) optional, for the other cells' labels.
 * Returns { key, value, rowItems, focusIndex } or null when the clicked cell is not a printed value.
 */
export function buildSourceQuery(params, headerFor) {
  if (!params || skipField(params.field) || !isLocatable(params.value)) return null;
  const group = 'row:' + String(params.id);
  const row = params.row || {};
  const rowItems = [];
  let focusIndex = -1;
  for (const [field, value] of Object.entries(row)) {
    if (skipField(field) || !isLocatable(value)) continue;
    const header = field === params.field ? (params.colDef && params.colDef.headerName) : (headerFor ? headerFor(field) : undefined);
    if (field === params.field) focusIndex = rowItems.length;
    rowItems.push({ key: labelFor(field, header), value, group });
  }
  if (focusIndex < 0) {
    focusIndex = rowItems.length;
    rowItems.push({ key: labelFor(params.field, params.colDef && params.colDef.headerName), value: params.value, group });
  }
  return { key: rowItems[focusIndex].key, value: params.value, rowItems, focusIndex };
}

/**
 * NFI-5884: a click on one row of a step's details list (the single-record view) -> the same query a grid cell click makes.
 * col: the clicked column ({ field, headerName }); record: the record the list shows; columns: the list's columns (labels for the other values).
 * Returns null when the clicked value is not a printed value, or there is nothing to read.
 */
export function buildDetailsSourceQuery(col, record, columns) {
  if (!col || !record) return null;
  const cols = Array.isArray(columns) ? columns : [];
  const headerFor = (f) => { const c = cols.find((dc) => dc && dc.field === f); return c ? c.headerName : undefined; };
  const id = record.id !== undefined && record.id !== null ? record.id : 'details';
  return buildSourceQuery({ field: col.field, value: record[col.field], id, row: record, colDef: col }, headerFor);
}
